import React, { Component, ReactNode } from 'react';
import { Wifi, RefreshCw, AlertCircle, Key } from 'lucide-react';
import { logger } from '../utils/logger';

interface Props {
  children: ReactNode;
  onApiError?: (error: Error) => void;
  fallbackComponent?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorType: 'network' | 'auth' | 'rate_limit' | 'generic' | null;
}

/**
 * Specialized Error Boundary for API-related errors
 * Provides specific error handling for different API failure scenarios
 */
export class ApiErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorType: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Analyze error to determine type
    let errorType: State['errorType'] = 'generic';
    
    if (error.message.includes('fetch')) {
      errorType = 'network';
    } else if (error.message.includes('API key') || error.message.includes('401') || error.message.includes('403')) {
      errorType = 'auth';
    } else if (error.message.includes('429') || error.message.includes('rate limit')) {
      errorType = 'rate_limit';
    }

    return {
      hasError: true,
      error,
      errorType
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log API error with context
    logger.error('API Error Boundary caught error:', {
      error: error.message,
      errorType: this.state.errorType,
      componentStack: errorInfo.componentStack
    });
    
    // Call parent error handler if provided
    if (this.props.onApiError) {
      this.props.onApiError(error);
    }

    // Save API error state for debugging
    try {
      const apiErrorLog = {
        error: error.message,
        errorType: this.state.errorType,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };
      localStorage.setItem('last_api_error', JSON.stringify(apiErrorLog));
    } catch (storageError) {
      logger.error('Failed to save API error log:', storageError);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorType: null
    });
  };

  handleReloadPage = () => {
    window.location.reload();
  };

  handleOfflineMode = () => {
    // Switch to offline mode (if implemented)
    this.setState({
      hasError: false,
      error: null,
      errorType: null
    });
    // TODO: Implement offline mode logic
  };

  getErrorContent() {
    const { error, errorType } = this.state;

    switch (errorType) {
      case 'network':
        return {
          icon: <Wifi className="w-12 h-12 text-orange-500" />,
          title: '인터넷 연결 오류',
          message: '인터넷 연결을 확인하고 다시 시도해주세요.',
          suggestions: [
            '인터넷 연결 상태를 확인하세요',
            'Wi-Fi 또는 데이터 연결을 다시 연결해보세요',
            '방화벽이나 프록시 설정을 확인하세요'
          ],
          actions: [
            { label: '다시 시도', action: this.handleRetry, primary: true },
            { label: '오프라인 모드', action: this.handleOfflineMode, primary: false }
          ]
        };

      case 'auth':
        return {
          icon: <Key className="w-12 h-12 text-red-500" />,
          title: 'API 키 인증 오류',
          message: 'API 키가 올바르지 않거나 만료되었습니다.',
          suggestions: [
            'API 키가 올바르게 입력되었는지 확인하세요',
            'API 키가 만료되지 않았는지 확인하세요',
            'Google Cloud Console에서 API 키 권한을 확인하세요'
          ],
          actions: [
            { label: '설정으로 이동', action: () => {/* TODO: Navigate to settings */}, primary: true },
            { label: '페이지 새로고침', action: this.handleReloadPage, primary: false }
          ]
        };

      case 'rate_limit':
        return {
          icon: <AlertCircle className="w-12 h-12 text-yellow-500" />,
          title: 'API 사용량 초과',
          message: 'API 호출 한도를 초과했습니다. 잠시 후 다시 시도해주세요.',
          suggestions: [
            '몇 분 후에 다시 시도해주세요',
            'API 사용량을 확인하세요',
            '더 작은 요청으로 나누어 시도해보세요'
          ],
          actions: [
            { label: '5분 후 재시도', action: () => setTimeout(this.handleRetry, 5 * 60 * 1000), primary: true },
            { label: '지금 재시도', action: this.handleRetry, primary: false }
          ]
        };

      default:
        return {
          icon: <AlertCircle className="w-12 h-12 text-gray-500" />,
          title: 'API 오류 발생',
          message: error?.message || '알 수 없는 오류가 발생했습니다.',
          suggestions: [
            '잠시 후 다시 시도해주세요',
            '문제가 계속되면 페이지를 새로고침해주세요',
            '개발자 도구에서 자세한 오류를 확인할 수 있습니다'
          ],
          actions: [
            { label: '다시 시도', action: this.handleRetry, primary: true },
            { label: '페이지 새로고침', action: this.handleReloadPage, primary: false }
          ]
        };
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallbackComponent) {
        return this.props.fallbackComponent;
      }

      const errorContent = this.getErrorContent();

      return (
        <div className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8">
          <div className="text-center max-w-lg mx-auto">
            <div className="mb-6">
              {errorContent.icon}
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {errorContent.title}
            </h3>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {errorContent.message}
            </p>

            {/* Error suggestions */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                해결 방법:
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 text-left list-disc list-inside space-y-1">
                {errorContent.suggestions.map((suggestion, index) => (
                  <li key={index}>{suggestion}</li>
                ))}
              </ul>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 justify-center">
              {errorContent.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.action}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    action.primary
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-gray-500 text-white hover:bg-gray-600'
                  }`}
                >
                  <RefreshCw className="w-4 h-4" />
                  {action.label}
                </button>
              ))}
            </div>

            {/* Development error details */}
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                  개발자 정보 보기
                </summary>
                <div className="mt-2 p-3 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono overflow-auto">
                  <p className="text-red-600 dark:text-red-400 mb-2">
                    {this.state.error?.message}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    Error Type: {this.state.errorType}
                  </p>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}